import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { initialState } from './state';
import { getStartEndDate } from 'src/utils/getStartEndDate';
import { AppState } from 'src/store';
import { BASE_URL } from 'src/config/api';
import { dateFormatter } from 'src/utils/dateFormatter';
import client from 'src/api/client';
import { Page } from 'src/admin-entity/interface/page/page-info.interface';
import { PointInfo } from 'src/admin-entity/interface/point/point-info.interface';
import { PointHistory } from 'src/admin-entity/interface/point/point-history.interface';
import { UserPointSpending } from 'src/admin-entity/interface/point/user-point-spending.interface';

type SimpleDateSelectType = 'yesterday' | 'today' | 'week' | 'month' | '3month' | '6month' | '1year' | 'allDate';

export const searchPointUsageList = createAsyncThunk(
    `pointSlice/searchPointUsageList`,
    async (_, { getState, rejectWithValue }) => {
        try {
            const {
                beginDate,
                endDate,
                memo,
                adminUserName,
                searchToken,
                searchTopic,
                page,
                take,
                userGradeList,
                searchType,
            } = (getState() as AppState).point.searchCondition;

            const url = new URL(`${BASE_URL}/v1/admin/point`);
            url.searchParams.append('page', `${page}`);
            url.searchParams.append('take', `${take}`);
            url.searchParams.append('beginDate', dateFormatter(new Date(beginDate), 'yyyy-MM-DD'));
            url.searchParams.append('endDate', dateFormatter(new Date(endDate), 'yyyy-MM-DD'));

            if (searchToken) {
                url.searchParams.append(searchTopic, searchToken);
            }
            if (adminUserName) {
                url.searchParams.append('adminUserName', adminUserName);
            }
            if (memo) {
                url.searchParams.append('memo', memo);
            }
            if (userGradeList.length > 0) {
                url.searchParams.append('appUserGradeId', userGradeList.join('|'));
            }
            if (searchType) {
                url.searchParams.append('searchType', searchType);
            }

            const res = await client.get<Page<UserPointSpending>>(url.toString());
            return res.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const getPointRewardListByExcel = createAsyncThunk(
    `pointSlice/getPointRewardListByExcel`,
    async (_, { getState, rejectWithValue }) => {
        try {
            const { beginDate, endDate, memo, page, take, adminUserName } = (getState() as AppState).point
                .rewardHistorySearchCondition;

            const url = new URL(`${BASE_URL}/v1/admin/point/issued/excel`);
            url.searchParams.append('page', `${page}`);
            url.searchParams.append('take', `${take}`);
            url.searchParams.append('beginDate', dateFormatter(new Date(beginDate), 'yyyy-MM-DD'));
            url.searchParams.append('endDate', dateFormatter(new Date(endDate), 'yyyy-MM-DD'));

            if (adminUserName) {
                url.searchParams.append('adminUserName', adminUserName);
            }

            if (memo) {
                url.searchParams.append('memo', memo);
            }

            const res = await client.get<Page<PointHistory>>(url.toString());
            return res.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const pointSlice = createSlice({
    name: 'pointSlice',
    initialState,
    reducers: {
        init: () => {
            return { ...initialState };
        },
        initSearchCondition: state => {
            state.searchCondition = initialState.searchCondition;
        },
        initLogSearchCondition: state => {
            state.rewardHistorySearchCondition = initialState.rewardHistorySearchCondition;
        },
        updateSearchType: (state, action: PayloadAction<string>) => {
            state.searchCondition.searchType = action.payload;
        },
        onClickSimpleDateSelector: (state, action: PayloadAction<SimpleDateSelectType>) => {
            state.searchCondition.selectedStartDateType = action.payload;

            const { startDate, endDate } = getStartEndDate(action.payload);
            state.searchCondition.beginDate =
                action.payload === 'allDate' ? new Date().toString() : startDate.toString();
            state.searchCondition.endDate = action.payload === 'allDate' ? new Date().toString() : endDate.toString();
        },
        onClickRewardHistorySimpleDateSelector: (state, action: PayloadAction<SimpleDateSelectType>) => {
            state.rewardHistorySearchCondition.selectedStartDateType = action.payload;

            const { startDate, endDate } = getStartEndDate(action.payload);
            state.rewardHistorySearchCondition.beginDate =
                action.payload === 'allDate' ? new Date().toString() : startDate.toString();
            state.rewardHistorySearchCondition.endDate =
                action.payload === 'allDate' ? new Date().toString() : endDate.toString();
        },
        updateBeginDate: (state, action: PayloadAction<string>) => {
            state.searchCondition.beginDate = action.payload.toString();
        },
        updateEndDate: (state, action: PayloadAction<string>) => {
            state.searchCondition.endDate = action.payload.toString();
        },
        updateRewardHistoryBeginDate: (state, action: PayloadAction<string>) => {
            state.searchCondition.beginDate = action.payload.toString();
        },
        updateRewardHistoryEndDate: (state, action: PayloadAction<string>) => {
            state.searchCondition.endDate = action.payload.toString();
        },
        updateRewardHistoryMemo: (state, action: PayloadAction<string>) => {
            state.rewardHistorySearchCondition.memo = action.payload;
        },
        updateRewardHistoryPage: (state, action: PayloadAction<number>) => {
            state.rewardHistorySearchCondition.page = action.payload;
        },
        updateRewardHistoryTake: (state, action: PayloadAction<number>) => {
            state.rewardHistorySearchCondition.take = action.payload;
        },
        updateRewardHistoryAdminUserName: (state, action: PayloadAction<string>) => {
            state.rewardHistorySearchCondition.adminUserName = action.payload;
        },
        updatePage: (state, action: PayloadAction<number>) => {
            state.searchCondition.page = action.payload;
        },
        updateTake: (state, action: PayloadAction<number>) => {
            state.searchCondition.take = action.payload;
        },
        updateGrade: (state, action: PayloadAction<number>) => {
            state.searchCondition.grade = action.payload;
        },
        updateSearchTopic: (state, action: PayloadAction<string>) => {
            state.searchCondition.searchTopic = action.payload;
        },
        updateSearchToken: (state, action: PayloadAction<string>) => {
            state.searchCondition.searchToken = action.payload;
        },
        updateMemo: (state, action: PayloadAction<string>) => {
            state.searchCondition.memo = action.payload;
        },
        updateAdminUserName: (state, action: PayloadAction<string>) => {
            state.searchCondition.adminUserName = action.payload;
        },
        updateSearchUserGradeList: (state, action: PayloadAction<number | string>) => {
            if (action.payload === 'all') {
                state.searchCondition.userGradeList = [1, 2, 3, 4, 5, 6];
                return;
            }
            if (action.payload === 'empty') {
                state.searchCondition.userGradeList = [];
                return;
            }

            if (state.searchCondition.userGradeList.includes(+action.payload)) {
                state.searchCondition.userGradeList = state.searchCondition.userGradeList.filter(
                    v => v !== action.payload,
                );
            } else {
                state.searchCondition.userGradeList = [...state.searchCondition.userGradeList, +action.payload];
            }
        },
    },
    extraReducers: builder => {
        builder.addCase(searchPointUsageList.fulfilled, (state, { payload }) => {
            state.pointList = payload;
        });
        builder.addCase(getPointRewardListByExcel.fulfilled, (state, { payload }) => {
            state.pointListWithExcel = payload;
        });
    },
});

export const {
    init,
    initSearchCondition,
    updateSearchType,
    onClickSimpleDateSelector,
    updateBeginDate,
    updateEndDate,
    updatePage,
    updateTake,
    updateGrade,
    updateSearchTopic,
    updateSearchToken,
    updateMemo,
    updateAdminUserName,
    updateSearchUserGradeList,
    initLogSearchCondition,
    updateRewardHistoryBeginDate,
    updateRewardHistoryEndDate,
    updateRewardHistoryMemo,
    updateRewardHistoryAdminUserName,
    onClickRewardHistorySimpleDateSelector,
    updateRewardHistoryPage,
    updateRewardHistoryTake,
} = pointSlice.actions;
export default pointSlice.reducer;
